import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

/**
 * Custom route reuse strategy that caches the home component.
 * This prevents the home component from being destroyed when navigating away,
 * preserving video state and other component data across navigation.
 */
export class CacheRouteReuseStrategy implements RouteReuseStrategy {
  private routeCache = new Map<string, DetachedRouteHandle>();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.isCachedRoute(route);
  }

  store(
    route: ActivatedRouteSnapshot,
    handle: DetachedRouteHandle | null
  ): void {
    if (this.isCachedRoute(route) && handle) {
      this.routeCache.set(this.getRouteKey(route), handle);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.isCachedRoute(route) && this.routeCache.has(this.getRouteKey(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.routeCache.get(this.getRouteKey(route)) ?? null;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  private isCachedRoute(route: ActivatedRouteSnapshot): boolean {
    const configuredPath = route.routeConfig?.path ?? '';

    if (configuredPath === 'home' || configuredPath.endsWith('/home')) {
      return true;
    }

    return route.url.some((segment) => segment.path === 'home');
  }

  private getRouteKey(route: ActivatedRouteSnapshot): string {
    const path = route.routeConfig?.path ?? '';
    const params = Object.keys(route.params)
      .sort()
      .map((key) => `${key}=${route.params[key]}`)
      .join('&');
    return params ? `${path}?${params}` : path;
  }
}
